"use client";
import { useEffect, useRef } from "react";
import { pushEvent } from "@/lib/analytics/gtm";
import { analyticsEvents } from "@/lib/analytics/events";

// Per-session cap so a misclassified region (e.g. decorative wrapper that
// reads as non-interactive) can't flood GA4 with thousands of dead_clicks.
const DEAD_CLICK_CAP = 50;

const INTERACTIVE_SELECTOR =
  'a,button,input,select,textarea,label,summary,[role="button"],[role="link"],[role="tab"],[role="menuitem"],[role="option"],[role="switch"],[role="checkbox"],[role="radio"],[onclick],[tabindex]';

export function ClickDelegator() {
  const deadCount = useRef(0);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (!target || target.nodeType !== 1) return;

      const zoneEl = target.closest<HTMLElement>("[data-track-zone]");
      const zone = zoneEl?.dataset.trackZone ?? "unknown";
      const anchor = target.closest<HTMLAnchorElement>("a[href]");
      const tag = target.tagName.toLowerCase();
      const text = (target as HTMLElement).innerText?.trim().slice(0, 60) || undefined;

      if (anchor) {
        try {
          const url = new URL(anchor.href, location.href);
          if (url.hostname && url.hostname !== location.hostname) {
            pushEvent(analyticsEvents.outbound_click, {
              href: anchor.href,
              hostname: url.hostname,
              zone,
            });
            return;
          }
        } catch {
          // malformed href — fall through to click_zone
        }
      }

      const interactive = target.closest(INTERACTIVE_SELECTOR);
      if (interactive) {
        pushEvent(analyticsEvents.click_zone, {
          zone,
          tag,
          text,
          href: anchor?.href,
        });
        return;
      }

      if (deadCount.current >= DEAD_CLICK_CAP) return;
      deadCount.current += 1;
      pushEvent(analyticsEvents.dead_click, {
        x: e.clientX,
        y: e.clientY,
        tag,
        zone,
      });
    };
    document.addEventListener("click", onClick, { capture: true });
    return () => document.removeEventListener("click", onClick, { capture: true });
  }, []);

  return null;
}
